/**
 * Guest-mode session scaffold.
 *
 * On first load we mint a guest session from the Worker and persist it in
 * localStorage. When real user accounts land, this module is the single
 * place that swaps to account tokens.
 */

import type { GuestSessionResponse } from '@klooma/shared';
import { apiUrl } from './config';

const STORAGE_KEY = 'klooma:session:v1';

export interface StoredSession {
  guestId: string;
  token: string;
  expiresAt: string;
}

let inflight: Promise<StoredSession> | null = null;

function readStored(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    // Refresh a day before expiry.
    if (new Date(parsed.expiresAt).getTime() - 86_400_000 < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export async function getSession(): Promise<StoredSession> {
  const stored = readStored();
  if (stored) return stored;
  inflight ??= createGuestSession().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function createGuestSession(): Promise<StoredSession> {
  const res = await fetch(apiUrl('/api/auth/guest'), { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to create guest session (${res.status})`);
  const body = (await res.json()) as GuestSessionResponse;
  const session: StoredSession = {
    guestId: body.guestId,
    token: body.token,
    expiresAt: body.expiresAt,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}
